import { getReportFooterText } from '../reportFooter';
import { normalizeDateString } from '@/utils/dateFormat';

export interface CustomerSurveyHeaderInfo {
  fromDate?: string | null;
  toDate?: string | null;
  userEmail?: string | null;
}

export interface CustomerSurveyItem {
  transactionDate: string;
  user: string;
  customer: string;
  missingEquipmentResponse?: string | null;
  missingEquipmentReason?: string | null;
  missingEquipmentDetails?: string | null;
  serviceSatisfaction?: string | null;
  staffHelpfulness?: string | null;
  processEfficiency?: string | null;
}

export interface CustomerSurveyTemplateOptions {
  generatedAt: Date;
  header: CustomerSurveyHeaderInfo;
  items: CustomerSurveyItem[];
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatEnum = (value: string | null | undefined): string => {
  if (!value) return '—';

  // Handle specific known values
  if (value === 'not_in_stock') return 'Not in Stock';
  if (value === 'na') return 'N/A';

  // Default: capitalize first letter of each word and replace underscores with spaces
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(normalizeDateString(value));
  if (Number.isNaN(date.getTime())) {
    return sanitizeText(value);
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildItemsTable = (items: CustomerSurveyItem[]): string => {
  if (items.length === 0) {
    return `
      <tr>
        <td colspan="9" class="empty-row">
          No customer survey responses matched the selected filters.
        </td>
      </tr>
    `;
  }

  return items
    .map(
      (item) => `
        <tr>
          <td>${formatDate(item.transactionDate)}</td>
          <td>${sanitizeText(item.user)}</td>
          <td>${sanitizeText(item.customer)}</td>
          <td>${formatEnum(item.missingEquipmentResponse)}</td>
          <td>${formatEnum(item.missingEquipmentReason)}</td>
          <td>${sanitizeText(item.missingEquipmentDetails)}</td>
          <td>${formatEnum(item.serviceSatisfaction)}</td>
          <td>${formatEnum(item.staffHelpfulness)}</td>
          <td>${formatEnum(item.processEfficiency)}</td>
        </tr>
      `,
    )
    .join('');
};

export const buildCustomerSurveyReportHtml = ({
  generatedAt,
  header,
  items,
}: CustomerSurveyTemplateOptions): string => {
  const generatedLabel = formatDateTime(generatedAt);
  const fromDateLabel = header.fromDate ? formatDate(header.fromDate) : '—';
  const toDateLabel = header.toDate ? formatDate(header.toDate) : '—';
  const userLabel = header.userEmail ? sanitizeText(header.userEmail) : 'All';
  const itemsTable = buildItemsTable(items);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>Customer Survey Report</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 16px;
      }

      .title {
        font-size: 24px;
        font-weight: 700;
        margin: 0;
        text-align: center;
      }

      .meta-row {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-top: 12px;
      }

      .meta-block {
        min-width: 220px;
      }

      .meta-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 2px;
      }

      .meta-value {
        font-size: 14px;
        color: #15263c;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 16px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 10px 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 24px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <h1 class="title">Customer Survey Report</h1>
      <div class="meta-row">
        <div class="meta-block">
          <div class="meta-label">From</div>
          <div class="meta-value">${fromDateLabel}</div>
        </div>
        <div class="meta-block">
          <div class="meta-label">To</div>
          <div class="meta-value">${toDateLabel}</div>
        </div>
        <div class="meta-block">
          <div class="meta-label">User</div>
          <div class="meta-value">${userLabel}</div>
        </div>
      </div>
    </header>

    <section>
      <table>
        <thead>
          <tr>
            <th>Date of Transaction</th>
            <th>User</th>
            <th>Customer</th>
            <th>Anything you couldn&apos;t receive today</th>
            <th>If Yes, why</th>
            <th>If Yes, what</th>
            <th>Satisfaction</th>
            <th>Staff Helpful / Professional</th>
            <th>How Efficient</th>
          </tr>
        </thead>
        <tbody>
          ${itemsTable}
        </tbody>
      </table>
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
